import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ClipboardList, Package, Truck, Download } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { authAPI } from '../../services/api';
import toast from 'react-hot-toast';
import brandMobileLogoUrl from '../../assets/branding/logo-sm.png';
import { usePwaInstall } from '../../hooks/usePwaInstall';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showResetRequest, setShowResetRequest] = useState(false);
  const [isRequestingReset, setIsRequestingReset] = useState(false);
  const [isCheckingResetStatus, setIsCheckingResetStatus] = useState(false);
  const [hasPendingResetRequest, setHasPendingResetRequest] = useState(false);
  const [resetRequestStatusMessage, setResetRequestStatusMessage] = useState('');
  const [resetRequestData, setResetRequestData] = useState({
    name: '',
    email: '',
  });
  const { login, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const { canInstall, isInstalling, promptInstall, showIosInstructions } = usePwaInstall();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await login(formData);
    if (success) {
      navigate('/dashboard');
    }
  };

  const handleChange = (e) => {
    const next = {
      ...formData,
      [e.target.name]: e.target.value,
    };
    setFormData(next);
    if (e.target.name === 'email') {
      setResetRequestData((prev) => ({ ...prev, email: e.target.value }));
      setResetRequestStatusMessage('');
    }
  };

  const handleInstallClick = async () => {
    await promptInstall();
  };

  const handleResetRequestChange = (e) => {
    setResetRequestData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleResetRequestSubmit = async (e) => {
    e.preventDefault();
    if (!resetRequestData.email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setIsRequestingReset(true);
    try {
      const response = await authAPI.requestPasswordReset({
        name: resetRequestData.name,
        email: resetRequestData.email.trim(),
      });
      const message = response?.data?.message || 'The request has been sent to Admin.';
      setResetRequestStatusMessage(message);
      setHasPendingResetRequest(true);
      setShowResetRequest(false);
      toast.success(message);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to send reset request');
    } finally {
      setIsRequestingReset(false);
    }
  };

  useEffect(() => {
    const email = String(formData.email || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setHasPendingResetRequest(false);
      return;
    }

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setIsCheckingResetStatus(true);
      try {
        const response = await authAPI.getPasswordResetRequestStatus(email);
        if (cancelled) return;
        const pending = Boolean(response?.data?.data?.pending);
        setHasPendingResetRequest(pending);
        if (pending) {
          setResetRequestStatusMessage('The request has been sent to Admin.');
          setShowResetRequest(false);
        }
      } catch (_) {
        if (!cancelled) {
          setHasPendingResetRequest(false);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingResetStatus(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [formData.email]);

  return (
    <div className="min-h-screen flex items-center justify-center  p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 max-w-5xl w-full shadow-xl rounded-xl overflow-hidden">

        {/* LEFT: LOGIN FORM */}
        <div className="bg-yellow-400/80 p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img
                src={brandMobileLogoUrl}
                alt="Foodmandu"
                className="h-20 w-13 object-contain"
              />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Foodmandu Onemart</h2>
            {/* <p className="mt-2 text-gray-700">
              Operations & Compliance Management
            </p> */}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="label">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="input"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="input pr-10"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-md font-semibold bg-black text-white hover:bg-yellow-800 hover:text-white transition duration-300 disabled:opacity-50"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>

            {!hasPendingResetRequest ? (
              <div className="text-right text-sm">
                <button
                  type="button"
                  onClick={() => setShowResetRequest((prev) => !prev)}
                  className="text-blue-700 hover:text-blue-800 hover:underline"
                >
                  {showResetRequest ? 'Hide password reset request' : 'Request Password Reset'}
                </button>
              </div>
            ) : null}

            {isCheckingResetStatus ? (
              <p className="text-xs text-gray-600">Checking reset request status...</p>
            ) : null}

            {resetRequestStatusMessage ? (
              <div className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
                {resetRequestStatusMessage}
              </div>
            ) : null}

            {showResetRequest ? (
              <div className="rounded-md border border-gray-300 bg-white p-3 space-y-3">
                <p className="text-xs text-gray-700">
                  Submit your email to send a reset request to Admin via notice board.
                </p>
                <div className="space-y-3">
                  <input
                    name="name"
                    type="text"
                    value={resetRequestData.name}
                    onChange={handleResetRequestChange}
                    className="input"
                    placeholder="Your name (optional)"
                  />
                  <input
                    name="email"
                    type="email"
                    required
                    value={resetRequestData.email}
                    onChange={handleResetRequestChange}
                    className="input"
                    placeholder="Your login email"
                  />
                  <button
                    type="button"
                    onClick={handleResetRequestSubmit}
                    disabled={isRequestingReset}
                    className="w-full py-2 rounded-md font-semibold bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50"
                  >
                    {isRequestingReset ? 'Sending...' : 'Send Request'}
                  </button>
                </div>
              </div>
            ) : null}

            {canInstall ? (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleInstallClick}
                  disabled={isInstalling}
                  className="w-full py-3 rounded-md font-semibold border border-gray-900 text-gray-900 bg-white hover:bg-gray-100 transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  {showIosInstructions ? 'Install App (iPhone/iPad)' : isInstalling ? 'Preparing install...' : 'Install App'}
                </button>
                {showIosInstructions ? (
                  <p className="text-xs text-gray-700 text-center">
                    On iPhone/iPad Safari: tap Share, then choose <span className="font-semibold">Add to Home Screen</span>.
                  </p>
                ) : null}
              </div>
            ) : null}
          </form>
        </div>

        {/* RIGHT: INFO PANEL */}
        <div className="hidden lg:flex flex-col justify-center p-10 text-white bg-gradient-to-br from-gray-900 via-gray-800 to-yellow-500">
          <h3 className="text-2xl font-bold mb-6">
            Manage Everything in One Place
          </h3>

          <div className="space-y-6">

            <div className="flex items-start gap-4">
              <ClipboardList className="h-8 w-8 text-yellow-300" />
              <div>
                <h4 className="font-semibold text-lg">Task Management</h4>
                <p className="text-sm text-gray-200">
                  Assign, track, and monitor operational tasks efficiently.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Package className="h-8 w-8 text-yellow-300" />
              <div>
                <h4 className="font-semibold text-lg">Inventory Management</h4>
                <p className="text-sm text-gray-200">
                  Maintain stock levels and manage product flow seamlessly.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Truck className="h-8 w-8 text-yellow-300" />
              <div>
                <h4 className="font-semibold text-lg">Logistics Management</h4>
                <p className="text-sm text-gray-200">
                  Optimize delivery processes and logistics operations.
                </p>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default Login;
